import React from "react";
import { SummaryElement } from "../atoms/summary-element";
import { ImageSlideshowElement } from "../basic/image-slideshow-element";
import { StoryElement, ImageGalleryElement } from "@quintype/components";
import { ImageLightbox } from "./lightbox/image-lightbox";
import get from "lodash/get";
import PropTypes from "prop-types";
import { PromotionalMessageElement } from "../basic/promotional-message-element";
import { getSubType } from "../../utils.js";
import { Blurb } from "../atoms/blurb";
import { Quote } from "../atoms/quote/index";
import { BlockQuote } from "../atoms/block-quote/index";
import { AlsoRead } from "../atoms/also-read/index";
import { QuestionAnswer } from "../atoms/q-and-a/index";
import { Attachment } from "../atoms/attachment/index";
import { truncate } from "../../../utils/truncate-string";
import { CustomLink } from "../custom-link";

const isFirstWhiteListed = (index, key) => {
  const whiteListObject = {
    0: {
      "also-read": true,
      quote: true,
      blockquote: true
    }
  };

  return get(whiteListObject, [index, key], false);
};

const StoryPageCard = props => {
  const elementAlignment = get(props, ["card", "metadata", "attributes", "elementalignment", 0], "").toLowerCase();
  function getElement(element, index, props) {
    const subType = getSubType(element);
    const { "attachment-element": attachmentElement = "default" } = get(
      props,
      ["config", "theme-attributes", "story-elements"],
      {}
    );
    const imageAttribution = element.title && element["image-attribution"] ? "card-caption-attribution" : "";
    switch (subType) {
      case "summary":
        return <SummaryElement element={element} />;
      case "blurb":
        return <Blurb element={element} key={element.id} />;
      case "blockquote":
        return (
          <div key={index}>
            <BlockQuote element={element} key={element.id} index={index} elementAlignment={elementAlignment} />
          </div>
        );
      case "quote":
        return <Quote element={element} elementAlignment={elementAlignment} index={index} />;
      case "image-gallery":
        const template = get(element, ["metadata", "type"]) === "gallery" ? ImageGalleryElement : ImageSlideshowElement;
        return (
          <div>
            <ImageLightbox template={template} element={element} story={props.story} cdn={props.cdn} />
          </div>
        );
      case "also-read":
        return <AlsoRead story={props.story} element={element} elementAlignment={elementAlignment} index={index} />;
      case "promotional-message":
        return <PromotionalMessageElement element={element} externalLink />;
      case "image":
        return (
          <div className={imageAttribution}>
            <ImageLightbox
              imageSlug={element["image-s3-key"]}
              cdn={props.cdn}
              template={StoryElement}
              element={element}
              story={props.story}
            />
          </div>
        );
      case "q-and-a":
        return <QuestionAnswer element={element} key={element.id} type={"q-and-a"} />;
      case "question":
        return <QuestionAnswer element={element} key={element.id} type={"question"} />;
      case "answer":
        return <QuestionAnswer element={element} key={element.id} type={"answer"} />;
      case "attachment":
        return <Attachment element={element} template={attachmentElement} key={element.id} />;
      default:
        return <StoryElement element={element} story={props.story} />;
    }
  }

  const firstElement = get(props, ["card", "story-elements", 0]);
  const infiniteScrollStyle = get(props, ["config", "theme-attributes", "infiniteScrollStyle"]);
  const cardStyle =
    elementAlignment && isFirstWhiteListed(0, firstElement.subtype) ? `card--${elementAlignment}` : "card-grid";

  if (infiniteScrollStyle === "read-more" && !props.firstStory) {
    return (
      <div data-testid="story-card" className={`card ${cardStyle} ${props.clazzName}`}>
        {(firstElement.type === "text" || firstElement.type === "title") && (
          <div key={firstElement.id} className={`read-more-card element-wrapper read-more-overlay`}>
            <bdi dangerouslySetInnerHTML={{ __html: truncate(firstElement.text, 400, true) }} />
            <div className="content-overlay" />
          </div>
        )}

        <div className={`read-more-btn card element-wrapper `}>
          <CustomLink href={`/${get(props, ["story", "slug"], "")}`}>Read More</CustomLink>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="story-card" className={`card  ${cardStyle} ${props.clazzName}`}>
      {props.card["story-elements"]
        .filter(element => element.id !== props.firstVideoElementID)
        .map((element, index) => {
          const removeMaxWidth = elementAlignment && isFirstWhiteListed(index, element.subtype) ? "remove-margin" : "";
          return (
            <div key={element.id} className={`element-wrapper ${removeMaxWidth}`}>
              {getElement(element, index, props)}
            </div>
          );
        })}
    </div>
  );
};

StoryPageCard.propTypes = {
  firstStory: PropTypes.bool,
  card: PropTypes.object,
  firstVideoElementID: PropTypes.string,
  story: PropTypes.object,
  cdn: PropTypes.string,
  clazzName: PropTypes.string,
  config: PropTypes.object
};

export { StoryPageCard };
